import { GridColDef } from '@mui/x-data-grid';
import {
  Box,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  Autocomplete,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { atom } from 'jotai';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

import { useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import isEqual from 'lodash/isEqual';
import EditStoreOwnerModal from '../EditStoreOwnerModal';
// TrackOrder modal removed; navigating to dedicated page instead
import Invoice from './Invoice';
import { useNavigate } from 'react-router-dom';

import { OrderListResponse, OrderResponse, AdminAssignDriverRequestSchemaModeEnum, DeliveryBoyWithLocation } from '../../../client-sdk';
import ErrorHandler from '../../Stateless/Common/ErrorHandler';
import CommonDataGrid from '../../Stateless/CommonDataGrid';
import { useGetAllOrders } from '../../Query/OrderManagementQuery';
import { OrderManagementParams } from './orderManagementParams';
import { useDebounce } from '../Utils/useDounce';
import { useAssignDriverApi } from '../../../hooks/useAssignDriverApi';
import { useSearchDeliveryBoysApi } from '../../../hooks/useSearchDeliveryBoysApi';
import { COLORS } from '../../../theme';
// import TrackOrder from './TrackOrder';

const statusOptions = [
  { value: '', label: '-- Select --' },
  // Use API enum values for query param, keep human-readable labels
  { value: 'orderPlaced', label: 'Order Placed' },
  { value: 'deliveryPartnerAssigned', label: 'Delivery Partner Assigned' },
  { value: 'deliveryPartnerAccepted', label: 'Delivery Partner Accepted' },
  { value: 'merchantAccepted', label: 'Merchant Accepted' },
  { value: 'merchantRejected', label: 'Merchant Rejected' },
  { value: 'orderPreparing', label: 'Order Preparing' },
  { value: 'orderDispatched', label: 'Order Dispatched' },
  { value: 'orderArrived', label: 'Order Arrived' },
  { value: 'orderDelivered', label: 'Order Delivered' },
  { value: 'orderCancelled', label: 'Order Cancelled' },
];

export interface DeliveryManagerFormValues {
  id?: string;
  question: string;
  questionInArabic: string;
  answer: string;
  answerInArabic: string;
  customerName: string;
}

interface OrderTableProps {
  orderData?: OrderListResponse;
  orderId: string;
  setOrderId: React.Dispatch<React.SetStateAction<string>>;
  orderStatus: string;
  setOrderStatus: React.Dispatch<React.SetStateAction<string>>;
  fromDate: string;
  setFromDate: React.Dispatch<React.SetStateAction<string>>;
  toDate: string;
  setToDate: React.Dispatch<React.SetStateAction<string>>;

}


export const deliveryManagerAtom = atom<DeliveryManagerFormValues | null>(null);
const OrderTable: React.FC<OrderTableProps> = ({ orderId,
  setOrderId,
  orderStatus,
  setOrderStatus, fromDate, setFromDate, toDate, setToDate }) => {
  // Track order popup removed in favor of navigation
  const navigate = useNavigate();
  const [openInvoice, setOpenInvoice] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [dateError, setDateError] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [orderModeFilter, setOrderModeFilter] = useState('');
  const [assignDriverDialogOpen, setAssignDriverDialogOpen] = useState(false);
  const [orderForDriverAssignment, setOrderForDriverAssignment] = useState<any>(null);

  const { params, dispatch } = OrderManagementParams();
  const memoizedParams = useMemo(() => params, [params]);

  const { data: axiosResponse, isLoading, refetch } = useGetAllOrders(memoizedParams);

  const latestData = axiosResponse?.data;

  // Store previous data to prevent unnecessary re-renders
  const dataRef = useRef<OrderResponse | undefined>(undefined);

  const orderData: OrderResponse | undefined = useMemo(() => {
    if (!isEqual(dataRef.current, latestData)) {
      dataRef.current = latestData;
    }
    return dataRef.current;
  }, [latestData]);

  const filteredRows = React.useMemo(() => {

    if (!orderData?.orders) return [];
    // const searchLower = String(params.search ?? '').toLowerCase().trim();
    let storeOwners = orderData?.orders || []
    
    // Client-side filtering by orderMode
    if (orderModeFilter && orderModeFilter !== '') {
      storeOwners = storeOwners.filter((row: any) => {
        const rowOrderMode = row.orderMode || 'normal';
        return rowOrderMode === orderModeFilter;
      });
    }

    return storeOwners.map((row, index) => {
      // Ensure we have a valid row object
      if (!row) return;
      
      // Type assertion to access customerInfo for Admin orders
      const adminOrder = row as any;
      const customerName = adminOrder.customerInfo?.name || 'N/A';
      
      // Extract order mode information
      const orderMode = (adminOrder as any).orderMode || 'normal';
      const preOrderDateTime = (adminOrder as any).preOrderDateTime || (adminOrder as any).preorderDateTime || null;
      
      return {
        ...row,
        id: `${row.orderId}-${index}`,
        sno: index + 1 + (Number(params.page) - 1) * Number(params.limit),
        invoiceAmount: row.invoiceDetails?.total || row.orderAmount || 0,
        orderDate: row.orderDate, // explicitly included
        customerName: customerName, // Map customer name from nested structure
        orderMode: orderMode, // Add order mode
        preOrderDateTime: preOrderDateTime // Add preorder date time
      };
    });

  }, [params.search, orderData, params.page, params.limit, orderModeFilter]);

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch]);


  // No-op; kept for reference if needed in future

  const handleCloseInvoice = () => {
    setOpenInvoice(false);
  };


  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // dispatch({ type: 'SET_SEARCH', payload: e.target.value });
    setSearchTerm(e.target.value)
  };

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, rowData: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowData(rowData);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowData(null);
  };

  const handleViewOrder = () => {
    // Navigate to track order page with order data
    navigate('/track-order', { 
      state: { 
        trackingOrder: selectedRowData 
      } 
    });
    handleMenuClose();
  };

  const handleEditOrder = () => {
    setSelectedOrder(selectedRowData);
    setOpenEditDialog(true);
    handleMenuClose();
  };

  const handleAssignDriver = () => {
    setOrderForDriverAssignment(selectedRowData);
    setAssignDriverDialogOpen(true);
    handleMenuClose();
  };

  // const handleFilterChange = (sortBy: string, sortOrder: 'asc' | 'desc') => {
  //   dispatch({ type: 'SET_SORT', payload: { sortBy, sortOrder } });
  // };
  const columns: GridColDef[] = [
    {
      field: 'sno',
      headerName: 'S.No',
      width: 70,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'customerName',
      headerName: 'Customer Name',
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
    },
    {
      field: 'orderNumber',
      headerName: 'Order ID',
      flex: 1,
      minWidth: 120,
      headerAlign: 'left',
    },
    {
      field: 'orderDate',
      headerName: 'Order Date',
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      valueFormatter: (params) => {
        const value = params;
        if (!value) return '-';

        const date = new Date(value);

        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;

        return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
      },
    },
    {
      field: 'orderAmount',
      headerName: 'Order Amount',
      flex: 1,
      minWidth: 120,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (params) => {
        return params != null ? `${params}` : '-';
      },
    },
    {
      field: 'invoiceAmount',
      headerName: 'Invoice Amount',
      flex: 1,
      minWidth: 120,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (params) => {
        return params != null ? `${params}` : '-';
      },
    },
    {
      field: 'currentStatus',
      headerName: 'Order Status',
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
    },
    {
      field: 'paymentStatus',
      headerName: 'Payment Status',
      flex: 1,
      minWidth: 120,
      headerAlign: 'left',
    },
    {
      field: 'orderMode',
      headerName: 'Order Mode',
      flex: 1,
      minWidth: 130,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const mode = params.value || 'normal';
        const isPreOrder = mode === 'preOrder' || mode === 'preorder';
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Chip
              label={isPreOrder ? 'Preorder' : 'Normal Order'}
              color={isPreOrder ? 'warning' : 'primary'}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                height: '24px',
                backgroundColor: isPreOrder ? '#ff9800' : '#2196f3',
                color: '#fff',
                '& .MuiChip-label': {
                  padding: '0 10px',
                },
                boxShadow: isPreOrder 
                  ? '0 2px 4px rgba(255, 152, 0, 0.3)' 
                  : '0 2px 4px rgba(33, 150, 243, 0.3)',
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'invoice',
      headerName: 'Invoice',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Tooltip title="View Invoice" arrow>
            <Button
              sx={{ textTransform: 'none' }}
              onClick={() => {
                setOpenInvoice(true);
                setSelectedInvoice(params.row);
              }}
            >
              View
            </Button>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Actions" arrow>
            <IconButton
              onClick={(e) => handleMenuOpen(e, params.row)}
              sx={{ 
                color: '#666',
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const handleSearchClick = () => {
    // Check for date validation before proceeding
    if (dateError) {
      return; // Don't proceed if there's a date error
    }

    const payload = {
      orderId: orderId.trim() === '' ? undefined : orderId.trim(),
      fromDate: fromDate === '' ? undefined : fromDate,
      toDate: toDate === '' ? undefined : toDate,
      status: orderStatus === '' ? undefined : orderStatus,
    };

    dispatch({ type: 'SET_FILTERS', payload });
    // Note: orderModeFilter is handled client-side, not sent to API
  };

  // --- Edit Dialog ---
  // const renderEditDialog = () => (
  //   <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="lg" fullWidth>
  //     <DialogTitle sx={{ fontWeight: 700 }}>Edit Order #{selectedOrder?.orderNumber || selectedOrder?.orderId}</DialogTitle>
  //     <DialogContent dividers sx={{ p: 0 }}>
  //       {/* Reuse TrackOrder read-only view inside modal for now, matching UI layout */}
  //       {selectedOrder && (
  //         <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
  //           {/* track view component */}
  //           {/* Importing directly would create a circular dep if it imports Query hooks used elsewhere; but TrackOrder is a pure presentational component */}
  //           {React.createElement(require('./TrackOrder').default, { trackingOrder: selectedOrder })}
  //         </Box>
  //       )}
  //       <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, p: 2 }}>
  //         <Button variant="outlined" onClick={() => setOpenEditDialog(false)}>Cancel</Button>
  //         <Button variant="contained" disabled>Save Changes</Button>
  //       </Box>
  //     </DialogContent>
  //   </Dialog>
  // );


  // Order ID input removed from UI per design. Keep state for compatibility but no input handler needed.

  // Date validation function
  const validateDates = (fromDate: string, toDate: string): string => {
    if (!fromDate || !toDate) return '';
    
    const from = new Date(fromDate);
    const to = new Date(toDate);
    
    if (to < from) {
      return 'To Date must be after From Date';
    }
    
    return '';
  };

  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFromDate = e.target.value;
    setFromDate(newFromDate);
    const error = validateDates(newFromDate, toDate);
    setDateError(error);
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newToDate = e.target.value;
    setToDate(newToDate);
    const error = validateDates(fromDate, newToDate);
    setDateError(error);
  };

  const handleReset = () => {
    setOrderId('');
    setFromDate('');
    setToDate('');
    setOrderStatus('');
    setOrderModeFilter('');
    setDateError('');
    dispatch({ type: 'RESET' }); // or manually clear each
    refetch()
  };



  return (
    <>
      <Box
        sx={{
          p: 2,
          border: '1px solid #e0e0e0',
          marginTop: '20px',
          color: '#666',
        }}
      >
        <Box
          sx={{
            p: 2,
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            background: '#f8f8f8',
            mb: 2,
          }}
        >
          <Typography
            sx={{ fontWeight: 500, fontSize: 18, color: '#888', mb: 2 }}
          >
            Search Filter
          </Typography>
          <Box component="form" sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ width: 120, fontWeight: 600, color: '#666' }}>
                  Order Status :
                </Typography>
                <TextField
                  select
                  size="small"
                  placeholder="-- Select --"
                  value={orderStatus}
                  onChange={(e) => setOrderStatus(e.target.value)}
                  sx={{
                    mb: 0,
                    '& .MuiInputBase-root': {
                      fontSize: 14,
                      color: 'text.primary',
                      width: '400px',
                      height: '40px',
                    },
                    '& .MuiFormLabel-root': {
                      fontSize: 14,
                    },
                  }}
                  SelectProps={{ native: true }}
                  variant="outlined"
                >
                  {statusOptions.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ width: 120, fontWeight: 600, color: '#666' }}>
                  Order Mode :
                </Typography>
                <TextField
                  select
                  size="small"
                  placeholder="-- Select --"
                  value={orderModeFilter}
                  onChange={(e) => setOrderModeFilter(e.target.value)}
                  sx={{
                    mb: 0,
                    '& .MuiInputBase-root': {
                      fontSize: 14,
                      color: 'text.primary',
                      width: '400px',
                      height: '40px',
                    },
                    '& .MuiFormLabel-root': {
                      fontSize: 14,
                    },
                  }}
                  SelectProps={{ native: true }}
                  variant="outlined"
                >
                  <option value="">-- All Orders --</option>
                  <option value="normal">Normal Order</option>
                  <option value="preOrder">Preorder</option>
                </TextField>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ width: 120, fontWeight: 600, color: '#666' }}>
                  From Date :
                </Typography>
                <TextField
                  size="small"
                  type="date"
                  value={fromDate}
                  onChange={handleFromDateChange}
                  sx={{
                    mb: 0,
                    '& .MuiInputBase-root': {
                      fontSize: 14,
                      color: 'text.primary',
                      width: '400px',
                      height: '40px',
                    },
                    '& .MuiFormLabel-root': {
                      fontSize: 14,
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ width: 120, fontWeight: 600, color: '#666' }}>
                  To Date :
                </Typography>
                <TextField
                  size="small"
                  type="date"
                  value={toDate}
                  onChange={handleToDateChange}
                  error={!!dateError}
                  helperText={dateError}
                  sx={{
                    mb: 0,
                    '& .MuiInputBase-root': {
                      fontSize: 14,
                      color: 'text.primary',
                      width: '400px',
                      height: '40px',
                    },
                    '& .MuiFormLabel-root': {
                      fontSize: 14,
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Box>
            </Box>
            {/* Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, gap: 2 }}>
              <Button
                variant="contained"
                disabled={!!dateError}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  bgcolor: COLORS.primary.main,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: COLORS.primary.main,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                  '&:disabled': {
                    bgcolor: '#ccc',
                    color: '#666',
                  },
                }}
                onClick={handleSearchClick}
              >
                Search
              </Button>
              <Button
                onClick={handleReset}
                variant="outlined"
                sx={{ textTransform: 'none', minWidth: 90, borderColor: COLORS.secondary.main, color: COLORS.secondary.main }}
              >
                Reset
              </Button>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            // p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2, // Optional: adds spacing between wrapped items
          }}
        >
          {/* Search - Left */}

          <Box
            sx={{
              width: 600,
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <TextField
              fullWidth
              placeholder="Search Customer Name, Order ID..."
              size="medium"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                '.MuiOutlinedInput-root': {
                  height: 40,
                  width: 600,
                  border: 'none', // remove inner border
                },
                '& fieldset': {
                  border: 'none', // remove Material-UI default border
                },
                '& .MuiInputBase-input': {
                  paddingTop: '10px', // adjust this value to move the placeholder lower
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 20, color: '#aaa', mt: '4px' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        <EditStoreOwnerModal
          open={openInvoice}
          onClose={handleCloseInvoice}
          title="Invoice"
        >
          <Invoice invoiceData={selectedInvoice} />
        </EditStoreOwnerModal>

        <CommonDataGrid
          key={`datagrid-page-${params.page}-limit-${params.limit}`}
          rows={filteredRows || []}
          columns={columns}
          pageSize={params.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          checkboxSelection
          loading={isLoading}
          page={params.page - 1}
          rowCount={orderData?.pagination?.totalItems || 0}
          components={{
            NoRowsOverlay: () => <ErrorHandler type="no-data" message="No categories found" onRetry={() => refetch()} />
          }}
        />
        
        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {(() => {
            // Fallback: if we somehow don't have row data, show only basic actions
            if (!selectedRowData) {
              return (
                <>
                  <MenuItem onClick={handleViewOrder}>
                    <ListItemIcon>
                      <VisibilityIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>View</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleEditOrder}>
                    <ListItemIcon>
                      <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Edit</ListItemText>
                  </MenuItem>
                </>
              );
            }

            const orderMode = (selectedRowData as any).orderMode || 'normal';
            const isPreOrder =
              orderMode === 'preOrder' || orderMode === 'preorder';

            const currentStatus = (selectedRowData as any).currentStatus || '';
            const blockedStatuses = [
              'Order Cancelled',
              'Merchant Accepted',
              'Order Placed',
            ];

            const acceptedDeliveryPartners =
              (selectedRowData as any)?.orderDeliveryDetails
                ?.acceptedDeliveryPartners || [];

            const shouldShowOnlyAssignDriver =
              isPreOrder &&
              !blockedStatuses.includes(currentStatus) &&
              Array.isArray(acceptedDeliveryPartners) &&
              acceptedDeliveryPartners.length === 0;

            const isBlockedStatus = blockedStatuses.includes(currentStatus);

            // For non-preorder orders, never show Assign Driver
            // Also for any blocked status, never show Assign Driver
            if (!isPreOrder || isBlockedStatus) {
              return (
                <>
                  <MenuItem onClick={handleViewOrder}>
                    <ListItemIcon>
                      <VisibilityIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>View</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleEditOrder}>
                    <ListItemIcon>
                      <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Edit</ListItemText>
                  </MenuItem>
                </>
              );
            }

            if (shouldShowOnlyAssignDriver) {
              // Preorder with no accepted partners and not in blocked statuses:
              // only show Assign Driver
              return (
                <MenuItem onClick={handleAssignDriver}>
                  <ListItemIcon>
                    <LocalShippingIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Assign Driver</ListItemText>
                </MenuItem>
              );
            }

            // Preorder default: show all three options
            return (
              <>
                <MenuItem onClick={handleViewOrder}>
                  <ListItemIcon>
                    <VisibilityIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>View</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleEditOrder}>
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Edit</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleAssignDriver}>
                  <ListItemIcon>
                    <LocalShippingIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Assign Driver</ListItemText>
                </MenuItem>
              </>
            );
          })()}
        </Menu>

        {/* Edit Dialog */}
        <EditOrderDialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} order={selectedOrder} />
        
        {/* Assign Driver Dialog from Table Actions */}
        {orderForDriverAssignment && (
          <AssignDriverDialog
            open={assignDriverDialogOpen}
            onClose={() => {
              setAssignDriverDialogOpen(false);
              setOrderForDriverAssignment(null);
            }}
            order={orderForDriverAssignment}
            onSuccess={() => {
              refetch();
            }}
          />
        )}
      </Box>
    </>
  );
};

// Assign Driver Dialog Component (Reusable)
const AssignDriverDialog: React.FC<{ 
  open: boolean; 
  onClose: () => void; 
  order: any;
  onSuccess?: () => void;
}> = ({ open, onClose, order, onSuccess }) => {
  
  const [assignmentMode, setAssignmentMode] = useState<'manual' | 'automatic'>('manual');
  const [reason, setReason] = useState('');
  const [driverOptions, setDriverOptions] = useState<DeliveryBoyWithLocation[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<DeliveryBoyWithLocation | null>(null);
  
  const { assignDriver, loading: assignLoading } = useAssignDriverApi();
  const { searchDeliveryBoys, loading: searchLoading } = useSearchDeliveryBoysApi();
  
  // Get store coordinates from order data
  const getStoreCoordinates = () => {
    const storePickupStatus = order?.storePickupStatuses?.[0];
    if (storePickupStatus) {
      return {
        latitude: parseFloat(storePickupStatus.storeLatitude),
        longitude: parseFloat(storePickupStatus.storeLongitude)
      };
    }
    return null;
  };
  
  // Search for delivery boys
  const searchDrivers = async (searchName?: string) => {
    const coordinates = getStoreCoordinates();
    if (!coordinates) {
      console.error('Store coordinates not found');
      return;
    }
    
    try {
      const drivers = await searchDeliveryBoys(
        searchName, 
        coordinates.latitude, 
        coordinates.longitude
      );
      setDriverOptions(drivers);
    } catch (error) {
      console.error('Failed to search drivers:', error);
      setDriverOptions([]);
    }
  };
  
  // Load initial drivers when popup opens
  useEffect(() => {
    if (open && assignmentMode === 'manual') {
      searchDrivers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, assignmentMode]);
  
  const handleAssignDriver = async () => {
    try {
      const requestData = {
        orderId: order?.orderId || order?._id,
        mode: assignmentMode === 'manual' ? AdminAssignDriverRequestSchemaModeEnum.Manual : AdminAssignDriverRequestSchemaModeEnum.Automatic,
        driverId: assignmentMode === 'manual' ? selectedDriver?.deliveryBoyId : undefined,
        reason: reason || undefined
      };
      
      const response = await assignDriver(requestData);
      console.log('Driver assigned successfully:', response);
      
      // Reset form
      setAssignmentMode('manual');
      setSelectedDriver(null);
      setReason('');
      setDriverOptions([]);
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
      
      // Close dialog
      onClose();
    } catch (error) {
      console.error('Failed to assign driver:', error);
      // You might want to show an error message here
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setAssignmentMode('manual');
    setSelectedDriver(null);
    setReason('');
    setDriverOptions([]);
    onClose();
  };
  
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: '1.2rem' }}>
        Assign Driver - Order #{order?.orderNumber || order?.orderId}
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Mode Selection */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <Typography sx={{ width: 120, fontWeight: 600, color: '#333', flexShrink: 0 }}>
              Mode:
            </Typography>
            <Box sx={{ flex: 1, minWidth: 400 }}>
              <RadioGroup
                value={assignmentMode}
                onChange={(e) => setAssignmentMode(e.target.value as 'manual' | 'automatic')}
                sx={{ flexDirection: 'row', gap: 2 }}
              >
                <FormControlLabel
                  value="manual"
                  control={<Radio />}
                  label="Manual"
                  sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500 } }}
                />
                <FormControlLabel
                  value="automatic"
                  control={<Radio />}
                  label="Auto"
                  sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500 } }}
                />
              </RadioGroup>
            </Box>
          </Box>

          {/* Driver Selection (Manual mode only) */}
          {assignmentMode === 'manual' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Typography sx={{ width: 120, fontWeight: 600, color: '#333', flexShrink: 0 }}>
                Select Driver:
              </Typography>
              <Box sx={{ flex: 1, minWidth: 400 }}>
                <Autocomplete
                  options={driverOptions}
                  value={selectedDriver}
                  onChange={(_, newValue) => {
                    setSelectedDriver(newValue);
                  }}
                  onInputChange={(_, newInputValue) => {
                    // Debounce search to avoid too many API calls
                    setTimeout(() => {
                      if (newInputValue.length > 2) {
                        searchDrivers(newInputValue);
                      } else if (newInputValue.length === 0) {
                        searchDrivers(); // Load all drivers when search is cleared
                      }
                    }, 300);
                  }}
                  getOptionLabel={(option) => option.name || ''}
                  openOnFocus
                  clearOnBlur
                  fullWidth
                  loading={searchLoading}
                  noOptionsText={searchLoading ? '' : 'No drivers found'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Driver"
                      size="small"
                      required
                      fullWidth
                      sx={{
                        '& .MuiInputBase-root': {
                          height: 40,
                          display: 'flex',
                          alignItems: 'center',
                          boxSizing: 'border-box',
                          width: '100%',
                        },
                        '& .MuiOutlinedInput-input': {
                          height: '100%',
                          padding: '0 14px',
                          boxSizing: 'border-box',
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          )}

          {/* Reason (Shown for both modes) */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%' }}>
            <Typography sx={{ width: 120, fontWeight: 600, color: '#333', mt: 1, flexShrink: 0 }}>
              Reason :
            </Typography>
            <Box sx={{ flex: 1, minWidth: 400 }}>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={5}
                placeholder="Enter reason for driver assignment"
                style={{
                  width: '100%',
                  fontSize: '14px',
                  padding: '8px 14px',
                  border: '1px solid #c4c4c4',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#1976d2';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#c4c4c4';
                }}
              />
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 2,
            mt: 2
          }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={assignLoading}
              sx={{ 
                textTransform: 'none',
                color: '#666',
                borderColor: '#ccc',
                '&:hover': {
                  borderColor: '#999',
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAssignDriver}
              disabled={assignLoading || (assignmentMode === 'manual' && !selectedDriver)}
              sx={{ 
                textTransform: 'none',
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0'
                }
              }}
            >
              {assignLoading ? 'Assigning...' : 'Assign Driver'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// Edit Order Dialog Component
const EditOrderDialog: React.FC<{ open: boolean; onClose: () => void; order: any }>
  = ({ open, onClose, order }) => {
  
  const [assignDriverOpen, setAssignDriverOpen] = useState(false);
  const [prescriptionPopupOpen, setPrescriptionPopupOpen] = useState(false);
  
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return `JOD ${amount?.toFixed(2) || '0.00'}`;
  };

  // const getStatusColor = (status: string) => {
  //   switch (status?.toLowerCase()) {
  //     case 'merchant accepted':
  //       return '#4CAF50';
  //     case 'order cancelled':
  //       return '#f44336';
  //     case 'order delivered':
  //       return '#4CAF50';
  //     case 'order preparing':
  //       return '#ff9800';
  //     default:
  //       return '#757575';
  //   }
  // };


  if (!order) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ 
        fontWeight: 700, 
        fontSize: '1.2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        Edit Order #{order?.orderNumber || order?.orderId}
        <Button
          onClick={onClose}
          sx={{ 
            minWidth: 'auto',
            p: 1,
            color: '#666',
            '&:hover': { backgroundColor: '#f5f5f5' }
          }}
        >
          ×
        </Button>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        {/* Customer Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
            Customer Information
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography><strong>Customer:</strong> {order.customerInfo?.name || 'N/A'}</Typography>
            <Typography><strong>Email:</strong> {order.customerInfo?.email || 'N/A'}</Typography>
            <Typography><strong>Phone:</strong> {order.shippingAddress?.phoneNumber || 'N/A'}</Typography>
          </Box>
        </Box>

        {/* Order Details */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
            Order Details
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Typography><strong>Date:</strong> {formatDate(order.orderDate)}</Typography>
            <Typography><strong>Amount:</strong> {formatCurrency(order.orderAmount)}</Typography>
            <Typography><strong>Payment:</strong> {order.paymentMethod || 'N/A'}</Typography>
            </Box>
            {/* Order Mode Information */}
            {(() => {
              const orderMode = (order as any).orderMode || 'normal';
              const isPreOrder = orderMode === 'preOrder' || orderMode === 'preorder';
              const preOrderDateTime = (order as any).preOrderDateTime || (order as any).preorderDateTime;
              
              return (
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  borderRadius: 1, 
                  backgroundColor: isPreOrder ? '#fff3e0' : '#e3f2fd',
                  border: `1px solid ${isPreOrder ? '#ff9800' : '#2196f3'}` 
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Chip
                      label={isPreOrder ? 'Preorder' : 'Normal Order'}
                      color={isPreOrder ? 'warning' : 'primary'}
                      size="medium"
                      sx={{ 
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        backgroundColor: isPreOrder ? '#ff9800' : '#2196f3',
                        color: '#fff',
                        boxShadow: isPreOrder 
                          ? '0 2px 6px rgba(255, 152, 0, 0.3)' 
                          : '0 2px 6px rgba(33, 150, 243, 0.3)',
                      }}
                    />
                    {isPreOrder && (
                      <Typography sx={{ fontSize: '0.875rem', color: '#666', fontWeight: 500 }}>
                        Scheduled for later delivery/pickup
                      </Typography>
                    )}
                    {!isPreOrder && (
                      <Typography sx={{ fontSize: '0.875rem', color: '#666', fontWeight: 500 }}>
                        Immediate order - ready for processing
                      </Typography>
                    )}
                  </Box>
                  {isPreOrder && preOrderDateTime && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography sx={{ fontSize: '0.875rem', color: '#666', mb: 0.5 }}>
                        <strong>Scheduled Date & Time:</strong>
                      </Typography>
                      <Typography sx={{ fontSize: '0.875rem', color: '#333', fontWeight: 500 }}>
                        {new Date(preOrderDateTime).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </Typography>
                    </Box>
                  )}
                  {!isPreOrder && (
                    <Typography sx={{ fontSize: '0.875rem', color: '#666', mt: 0.5 }}>
                      Immediate order - ready for processing
                    </Typography>
                  )}
                </Box>
              );
            })()}
          </Box>
        </Box>

        {/* Tracking History */}
        {order.trackingHistory && order.trackingHistory.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
              Tracking History
            </Typography>
            <Box sx={{ 
              border: '1px solid #e0e0e0', 
              borderRadius: 1,
              p: 3,
              backgroundColor: '#fafafa'
            }}>
              {/* Horizontal Timeline */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {order.trackingHistory.map((historyItem: any, index: number) => {
                  const isLast = index === order.trackingHistory.length - 1;
                  const isCompleted = !isLast || order.currentStatus === 'Order Delivered';
                  const isRejected = historyItem.status?.toLowerCase().includes('rejected') || 
                                   historyItem.status?.toLowerCase().includes('cancelled');
                  
                  const getStatusColor = () => {
                    if (isRejected) return '#f44336';
                    if (isCompleted) return '#4CAF50';
                    return '#757575';
                  };

                  const getStatusIcon = () => {
                    const status = historyItem.status?.toLowerCase();
                    if (status?.includes('placed')) return '🍽️';
                    if (status?.includes('assigned') || status?.includes('accepted')) return '✓';
                    if (status?.includes('preparing')) return '📦';
                    if (status?.includes('ready') || status?.includes('pickup')) return '✓';
                    if (status?.includes('dispatched') || status?.includes('shipped')) return '🚚';
                    if (status?.includes('delivered')) return '👤';
                    if (status?.includes('rejected') || status?.includes('cancelled')) return '✕';
                    return '📋';
                  };

                  return (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      position: 'relative',
                      flex: 1,
                      minWidth: 0
                    }}>
                      {/* Status Circle */}
                      <Box sx={{ 
                        width: 50, 
                        height: 50, 
                        borderRadius: '50%', 
                        backgroundColor: getStatusColor(),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        mb: 1,
                        zIndex: 2,
                        position: 'relative'
                      }}>
                        {getStatusIcon()}
                      </Box>
                      
                      {/* Status Text */}
                      <Typography sx={{ 
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        color: '#333',
                        textAlign: 'center',
                        lineHeight: 1.2,
                        maxWidth: '80px'
                      }}>
                        {historyItem.status}
                      </Typography>
                      
                      {/* Connecting Line (except for last item) */}
                      {!isLast && (
                        <Box sx={{
                          position: 'absolute',
                          top: 25, // Center of the circle
                          left: 'calc(50% + 25px)', // Start from right edge of circle
                          right: 'calc(-50% + 25px)', // End at left edge of next circle
                          height: '2px',
                          backgroundColor: getStatusColor(),
                          zIndex: 1
                        }} />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}

        {/* Prescription Section */}
        {order.prescriptionUrl && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                Prescription
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Box component="span">👁️</Box>}
                onClick={() => setPrescriptionPopupOpen(true)}
                sx={{
                  borderColor: COLORS.primary.main,
                  color: COLORS.primary.main,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 2,
                  py: 0.5,
                  minWidth: 'auto',
                  '&:hover': {
                    borderColor: COLORS.primary.dark,
                    backgroundColor: `${COLORS.primary.main}08`
                  }
                }}
              >
                View
              </Button>
            </Box>
          </Box>
        )}

        {/* Item Details Table */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
            Item Details
          </Typography>
            <Box sx={{ 
              border: '1px solid #e0e0e0', 
            borderRadius: 1,
            overflow: 'hidden'
          }}>
            {/* Table Header */}
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: '1.2fr 2.5fr 1fr 0.8fr 1.2fr 1.2fr 1fr',
              backgroundColor: '#f5f5f5',
              p: 1.5,
              fontWeight: 600,
              borderBottom: '1px solid #e0e0e0'
            }}>
              <Typography>Store</Typography>
              <Typography>Item</Typography>
              <Typography>Variant</Typography>
              <Typography>Quantity</Typography>
              <Typography>Unit Price</Typography>
              <Typography>Total</Typography>
              <Typography>Status</Typography>
            </Box>
            
            {/* Grouped Items by Store */}
            {(() => {
              // Get accepted and denied item IDs from order level
              const acceptedItemIds = order.acceptedItemIds || [];
              const deniedItemIds = order.deniedItemIds || [];
              
              // Group items by store and map merchantAccepted status
              const groupedItems = order.orderItems?.reduce((acc: any, item: any) => {
                const storeKey = item.storeId || item.storeName;
                if (!acc[storeKey]) {
                  acc[storeKey] = {
                    storeName: item.storeName,
                    storeType: item.storeType || 'Restaurant',
                    items: []
                  };
                }
                
                // Determine merchantAccepted status based on item ID
                const itemId = item.itemId || item.id;
                let merchantAccepted: boolean;
                
                // Prioritize order-level arrays (acceptedItemIds/deniedItemIds) as source of truth
                if (acceptedItemIds.length > 0 || deniedItemIds.length > 0) {
                  // Use arrays to determine status
                  if (acceptedItemIds.includes(itemId)) {
                    merchantAccepted = true;
                  } else if (deniedItemIds.includes(itemId)) {
                    merchantAccepted = false;
                  } else {
                    // Item not in either array - use item's merchantAccepted if available, otherwise default to false
                    merchantAccepted = item.merchantAccepted ?? false;
                  }
                } else {
                  // Arrays not available, use item's merchantAccepted field
                  merchantAccepted = item.merchantAccepted ?? false;
                }
                
                // Create item with correct merchantAccepted status
                const itemWithStatus = {
                  ...item,
                  merchantAccepted
                };
                
                acc[storeKey].items.push(itemWithStatus);
                return acc;
              }, {}) || {};

              return Object.keys(groupedItems).length > 0 ? (
                Object.entries(groupedItems).flatMap(([storeKey, storeData]: [string, any], storeIndex: number) =>
                  storeData.items.map((item: any, itemIndex: number) => (
                        <Box key={`${storeKey}-${itemIndex}`} sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: '1.2fr 2.5fr 1fr 0.8fr 1.2fr 1.2fr 1fr',
                          p: 1.5,
                          borderBottom: '1px solid #e0e0e0',
                          borderTop: storeIndex > 0 && itemIndex === 0 ? '2px solid #e0e0e0' : 'none',
                          alignItems: 'center',
                          backgroundColor: '#fafafa'
                        }}>
                          {/* Store Name - show only on first item of each store group */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            {itemIndex === 0 && (
                              <>
                                <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '0.875rem' }}>
                                  {storeData.storeName}
                                </Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: '#666', mt: 0.25 }}>
                                  {storeData.storeType}
                                </Typography>
                              </>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {item.imageUrl && (
                              <Box
                                component="img"
                                src={item.imageUrl[0]}
                                alt={item.itemName}
                                sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover' }}
                              />
                            )}
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 520 }}>
                                {item.itemName}
                              </Typography>
                            {Array.isArray(item?.choices) && item.choices.length > 0 && (
                              <Typography sx={{ fontSize: '0.85rem', color: '#666', mt: 0.25 }}>
                                {'+ '}
                                {item.choices
                                  .map((c: any) => `${c?.choiceName} (${formatCurrency(Number(c?.choicePrice ?? 0))})`)
                                  .join(', ')}
                              </Typography>
                            )}
                            {item.specialInstructions && item.specialInstructions.trim() !== 'N/A' && (
                              <Typography sx={{ fontSize: '0.85rem', color: '#666', mt: 0.25 }}>
                                <strong>Special Instruction:</strong> {item.specialInstructions}
                              </Typography>
                            )}
                            </Box>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '0.875rem', color: '#666' }}>
                              {(() => {
                                // Try multiple possible variant fields to support different payload shapes
                                const rawVariant =
                                  item.itemVariant ||
                                  item.variant ||
                                  item.variantName ||
                                  item.selectedVariant ||
                                  (Array.isArray(item.variants) && item.variants[0]?.name) ||
                                  '';

                                const variantValue =
                                  typeof rawVariant === 'string'
                                    ? rawVariant.trim()
                                    : rawVariant;

                                // If variant is empty or explicitly "N/A", don't show the text at all
                                if (
                                  !variantValue ||
                                  (typeof variantValue === 'string' &&
                                    (variantValue === 'N/A' ||
                                      variantValue === 'NA' ||
                                      variantValue.toLowerCase() === 'n/a'))
                                ) {
                                  return '';
                                }

                                return variantValue;
                              })()}
                            </Typography>
                          </Box>
                          <Typography>{item.quantity || 1}</Typography>
                          <Typography>
                            {(() => {
                              const unitPrice =
                                Number(
                                  item.discountedPrice ??
                                  item.originalPrice ??
                                  0
                                ) || 0;

                              const hasPriceSource =
                                typeof item.total === 'number' ||
                                item.discountedPrice != null ||
                                item.originalPrice != null;

                              // If there is no real price information, avoid showing a misleading 0 total
                              if (!hasPriceSource) {
                                return '-';
                              }

                              return formatCurrency(unitPrice);
                            })()}
                          </Typography>
                          <Typography sx={{ fontWeight: 600 }}>
                            {(() => {
                              const quantity = Number(item.quantity ?? 1) || 1;
                              const unitPrice =
                                Number(
                                  item.discountedPrice ??
                                  item.originalPrice ??
                                  0
                                ) || 0;

                              const hasPriceSource =
                                typeof item.total === 'number' ||
                                item.discountedPrice != null ||
                                item.originalPrice != null;

                              // If there is no real price information, avoid showing a misleading 0 total
                              if (!hasPriceSource) {
                                return '-';
                              }

                              const total =
                                typeof item.total === 'number' && !Number.isNaN(item.total)
                                  ? item.total
                                  : unitPrice * quantity;
                              return formatCurrency(total);
                            })()}
                          </Typography>
                          <Box>
                            <Chip
                              label={item.merchantAccepted ? 'Accepted' : 'Rejected'}
                              color={item.merchantAccepted ? 'success' : 'error'}
                              size="small"
                              variant="filled"
                            />
                          </Box>
                        </Box>
                      ))
                )
              ) : (
                <Box sx={{ p: 2, textAlign: 'center', color: '#666' }}>
                  No items found
                </Box>
              );
            })()}
          </Box>
        </Box>

        {/* Order Summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
            Order Summary
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography><strong>Total Items:</strong> {order.orderItems?.length || 0}</Typography>
            <Typography><strong>Order Amount:</strong> {formatCurrency(order.orderAmount)}</Typography>
            <Typography><strong>Invoice Amount:</strong> {formatCurrency(order.invoiceDetails?.total || order.orderAmount)}</Typography>
          </Box>
        </Box>
      </DialogContent>
      
      {/* Action Buttons */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: 2, 
        p: 2,
        borderTop: '1px solid #e0e0e0'
      }}>
        <Button 
          variant="outlined" 
          onClick={onClose}
          sx={{ 
            textTransform: 'none',
            color: '#f44336',
            borderColor: '#f44336',
            '&:hover': {
              borderColor: '#d32f2f',
              backgroundColor: 'rgba(244, 67, 54, 0.04)'
            }
          }}
        >
          CANCEL
        </Button>
              <Button
                variant="outlined"
          onClick={() => setAssignDriverOpen(true)}
                sx={{ 
                  textTransform: 'none',
            borderColor: COLORS.primary.main,
            color: COLORS.primary.main,
                  '&:hover': {
              borderColor: COLORS.primary.dark,
              backgroundColor: `${COLORS.primary.main}08`
                  }
                }}
          startIcon={<LocalShippingIcon />}
              >
          Assign Driver
              </Button>
              <Button
                variant="contained"
          disabled
                sx={{ 
                  textTransform: 'none',
            backgroundColor: '#ccc',
            color: '#666',
                  '&:hover': {
              backgroundColor: '#ccc'
                  }
                }}
          startIcon={<Box component="span">🔒</Box>}
              >
          SAVE CHANGES
              </Button>
            </Box>
      
      {/* Assign Driver Dialog - Using Reusable Component */}
      <AssignDriverDialog
        open={assignDriverOpen}
        onClose={() => setAssignDriverOpen(false)}
        order={order}
        onSuccess={() => {
          // Optionally refresh order data or show success message
        }}
      />

      {/* Prescription Popup Modal */}
      <Dialog 
        open={prescriptionPopupOpen} 
        onClose={() => setPrescriptionPopupOpen(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 700, 
          fontSize: '1.2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1
        }}>
          Prescription View
          <Button
            onClick={() => setPrescriptionPopupOpen(false)}
            sx={{ 
              minWidth: 'auto',
              p: 1,
              color: '#666',
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
          >
            ×
          </Button>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Box sx={{ 
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            backgroundColor: '#f5f5f5'
          }}>
            <Box
              component="img"
              src={order.prescriptionUrl}
              alt="Prescription"
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: 1,
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const errorDiv = document.createElement('div');
                errorDiv.innerHTML = `
                  <div style="text-align: center; padding: 40px; color: #666;">
                    <div style="font-size: 48px; margin-bottom: 16px;">📄</div>
                    <div style="font-size: 18px; font-weight: 500; margin-bottom: 8px;">Unable to load prescription</div>
                    <div style="font-size: 14px;">The prescription image could not be displayed</div>
                  </div>
                `;
                target.parentNode?.appendChild(errorDiv);
              }}
            />
          </Box>
        </DialogContent>
        
        {/* <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 2, 
          p: 2,
          borderTop: '1px solid #e0e0e0'
          }}>
          <Button 
            variant="outlined" 
            onClick={() => setPrescriptionPopupOpen(false)}
            sx={{ 
              textTransform: 'none',
              color: '#666',
              borderColor: '#ccc',
              '&:hover': {
                borderColor: '#999',
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Close
          </Button>
          <Button 
            variant="contained" 
            onClick={() => window.open(order.prescriptionUrl, '_blank')}
            sx={{ 
              textTransform: 'none',
              backgroundColor: '#2196F3',
              '&:hover': {
                backgroundColor: '#1976D2'
              }
            }}
          >
            Open in New Tab
          </Button>
        </Box> */}
      </Dialog>
    </Dialog>
  );
};

export default OrderTable;

// Usage of inline edit dialog
// Keep component export above; here we render dialog via portal next to the table